import { useMemo, useState } from 'react';
import SearchInput from '../common/search-input';

export interface SelectionPanelOption {
  id: string;
  label: string;
  meta?: string;
}

interface SelectionPanelProps {
  title: string;
  helper?: string;
  options: SelectionPanelOption[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}

/**
 * Dual-panel picker.
 * LEFT: search box + scrollable checkbox list (filtered by label/meta).
 * RIGHT: "Selected (N)" panel with removable chips.
 * Pure Tailwind, dark-mode aware, no external dependency.
 */
const SelectionPanel = ({ title, helper, options, selectedIds, onChange }: SelectionPanelProps) => {
  const [search, setSearch] = useState('');

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.meta ? o.meta.toLowerCase().includes(q) : false)
    );
  }, [options, search]);

  const selectedOptions = useMemo(
    () => options.filter((o) => selectedSet.has(o.id)),
    [options, selectedSet]
  );

  const toggle = (id: string) => {
    if (selectedSet.has(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const remove = (id: string) => {
    onChange(selectedIds.filter((x) => x !== id));
  };

  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between">
        <label className="text-sm font-medium text-gray-800 dark:text-gray-200">{title}</label>
        {helper && <span className="text-xs text-ink-muted">{helper}</span>}
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {/* LEFT: searchable checkbox list */}
        <div className="ds-card">
          <div className="p-2 border-b border-line">
            <SearchInput value={search} onChange={setSearch} width="100%" />
          </div>

          <div className="max-h-64 overflow-auto p-1">
            {filteredOptions.length === 0 ? (
              <p className="p-3 text-center text-xs text-ink-muted">
                No options found
              </p>
            ) : (
              filteredOptions.map((o) => {
                const checked = selectedSet.has(o.id);
                return (
                  <label
                    key={o.id}
                    className={`flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm transition-colors ${
                      checked
                        ? 'bg-indigo-50 dark:bg-indigo-900/30'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(o.id)}
                      className="h-4 w-4 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-gray-900 dark:text-gray-100">
                        {o.label}
                      </span>
                      {o.meta && (
                        <span className="block truncate text-xs text-ink-muted">
                          {o.meta}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </div>

        {/* RIGHT: selected chips */}
        <div className="rounded-md border border-line bg-canvas dark:bg-gray-900/40">
          <div className="border-b border-line px-3 py-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Selected ({selectedOptions.length})
            </span>
          </div>
          <div className="max-h-64 overflow-auto p-2">
            {selectedOptions.length === 0 ? (
              <p className="p-3 text-center text-xs text-ink-muted">
                Nothing selected yet. Pick items from the left.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {selectedOptions.map((o) => (
                  <span
                    key={o.id}
                    className="inline-flex items-center gap-1 rounded-full bg-indigo-100 dark:bg-indigo-900/50 px-2.5 py-1 text-xs font-medium text-indigo-800 dark:text-indigo-200"
                  >
                    <span className="max-w-[160px] truncate">{o.label}</span>
                    <button
                      type="button"
                      onClick={() => remove(o.id)}
                      className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full hover:bg-indigo-200 dark:hover:bg-indigo-800"
                      aria-label={`Remove ${o.label}`}
                    >
                      <i className="pi pi-times" style={{ fontSize: '0.6rem' }} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectionPanel;
